import React, { useEffect } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import RecordVoiceOverRoundedIcon from "@mui/icons-material/RecordVoiceOverRounded";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import SyncRoundedIcon from "@mui/icons-material/SyncRounded";
import { useAuth } from "../../context/AuthContext";
import {
  usePatchVoiceSettings,
  usePreviewVoiceAssistant,
  useSyncVoiceAssistant,
  useVoiceSettings,
} from "../../hooks/useVoiceSettings";
import { useVoiceSettingsStore } from "../../store/voiceSettingsSlice";
import type { VoiceTonePreset } from "../../api/voiceSettings";
import { VoiceStatusSummary } from "./VoiceStatusSummary";
import { VoiceAssistantPreview } from "./VoiceAssistantPreview";
import { ChatStatusSummary } from "./ChatStatusSummary";

const TONE_OPTIONS: Array<{ value: VoiceTonePreset; label: string }> = [
  { value: "warm_professional", label: "Warm professional" },
  { value: "calm_concise", label: "Calm concise" },
  { value: "upbeat_friendly", label: "Upbeat friendly" },
  { value: "premium_polished", label: "Premium polished" },
  { value: "custom", label: "Custom" },
];

const TONE_LABELS = Object.fromEntries(
  TONE_OPTIONS.map((option) => [option.value, option.label])
) as Record<VoiceTonePreset, string>;

function getErrorMessage(err: unknown): string {
  if (
    typeof err === "object" &&
    err !== null &&
    "response" in err &&
    typeof err.response === "object" &&
    err.response !== null &&
    "data" in err.response
  ) {
    const data = err.response.data as { safe_error?: string; error?: string };
    return data.safe_error || data.error || "Failed to update AI assistant settings.";
  }
  return err instanceof Error
    ? err.message
    : "Failed to update AI assistant settings.";
}

export const VoiceSettingsTab: React.FC = () => {
  const { businessId } = useAuth();
  const { data: settings, isLoading, isError } = useVoiceSettings(businessId);
  const patchMutation = usePatchVoiceSettings(businessId);
  const previewMutation = usePreviewVoiceAssistant(businessId);
  const syncMutation = useSyncVoiceAssistant(businessId);
  const {
    draft,
    isDirty,
    fieldErrors,
    formError,
    initDraft,
    updateDraft,
    resetDraft,
    setFieldErrors,
    setFormError,
  } = useVoiceSettingsStore();

  useEffect(() => {
    if (settings) initDraft(settings);
  }, [settings, initDraft]);

  if (!businessId) {
    return <Alert severity="warning">Business context is not available.</Alert>;
  }

  if (isLoading || !settings || !draft) {
    return (
      <Card variant="outlined">
        <CardContent>
          <Skeleton variant="text" width="30%" height={28} />
          <Skeleton variant="text" width="65%" sx={{ mt: 1 }} />
          <Skeleton variant="rectangular" height={180} sx={{ mt: 2 }} />
        </CardContent>
      </Card>
    );
  }

  if (isError) {
    return <Alert severity="error">Failed to load voice settings.</Alert>;
  }

  const greetingRemaining =
    settings.assistant_greeting_max_length - draft.assistant_greeting.length;
  const chatGreetingRemaining =
    settings.chat_greeting_max_length - draft.chat_greeting.length;
  const guidanceRemaining =
    settings.tone_custom_instructions_max_length -
    draft.tone_custom_instructions.length;
  const canShowSync = settings.can_sync && settings.status === "sync_pending";

  const handleSave = () => {
    setFormError(null);
    setFieldErrors({});
    if (
      draft.chat_greeting.length > settings.chat_greeting_max_length
    ) {
      setFieldErrors({
        chat_greeting: "Chat greeting must be 250 characters or less.",
      });
      return;
    }
    if (
      draft.assistant_greeting.length > settings.assistant_greeting_max_length
    ) {
      setFieldErrors({
        assistant_greeting: "Greeting must be 250 characters or less.",
      });
      return;
    }
    if (
      draft.tone_custom_instructions.length >
      settings.tone_custom_instructions_max_length
    ) {
      setFieldErrors({
        tone_custom_instructions: "Extra guidance must be 500 characters or less.",
      });
      return;
    }
    patchMutation.mutate(
      {
        tone_preset: draft.tone_preset,
        chat_greeting: draft.chat_greeting,
        assistant_greeting: draft.assistant_greeting,
        tone_custom_instructions: draft.tone_custom_instructions,
      },
      {
        onError: (err) => setFormError(getErrorMessage(err)),
      }
    );
  };

  return (
    <Box sx={{ maxWidth: 820 }}>
      <Stack spacing={3}>
        <Box>
          <Typography sx={{ fontWeight: 800, color: "#0F172A", fontSize: "1.25rem" }}>
            AI assistant
          </Typography>
          <Typography sx={{ mt: 0.75, fontSize: "0.92rem", color: "#475569" }}>
            {settings.assistant_summary}
          </Typography>
        </Box>

        <Card variant="outlined" sx={{ borderRadius: "14px" }}>
          <CardContent sx={{ p: 3 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
              <RecordVoiceOverRoundedIcon sx={{ color: "#C9734F" }} />
              <Typography sx={{ fontWeight: 700, color: "#0F172A" }}>
                Assistant style
              </Typography>
            </Box>

            <Stack spacing={2.5}>
              <FormControl size="small" fullWidth>
                <InputLabel id="assistant-style-label">Style</InputLabel>
                <Select
                  labelId="assistant-style-label"
                  label="Style"
                  value={draft.tone_preset}
                  renderValue={(value) => TONE_LABELS[value as VoiceTonePreset]}
                  onChange={(event) =>
                    updateDraft({
                      tone_preset: event.target.value as VoiceTonePreset,
                    })
                  }
                >
                  {TONE_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                label="Chat greeting"
                value={draft.chat_greeting}
                onChange={(event) =>
                  updateDraft({ chat_greeting: event.target.value })
                }
                error={!!fieldErrors.chat_greeting}
                helperText={
                  fieldErrors.chat_greeting ||
                  "Used as guidance for new webchat and messaging conversations. Keep it short and natural."
                }
                fullWidth
                inputProps={{ maxLength: settings.chat_greeting_max_length }}
              />
              <Typography
                sx={{
                  alignSelf: "flex-end",
                  fontSize: "0.75rem",
                  color: chatGreetingRemaining < 0 ? "#DC2626" : "#94A3B8",
                }}
              >
                {draft.chat_greeting.length} / {settings.chat_greeting_max_length}{" "}
                characters
              </Typography>

              <TextField
                label="Voice greeting"
                value={draft.assistant_greeting}
                onChange={(event) =>
                  updateDraft({ assistant_greeting: event.target.value })
                }
                error={!!fieldErrors.assistant_greeting}
                helperText={
                  fieldErrors.assistant_greeting ||
                  "The first thing callers hear. Voice changes need to be synced before calls use them."
                }
                fullWidth
                inputProps={{ maxLength: settings.assistant_greeting_max_length }}
              />
              <Typography
                sx={{
                  alignSelf: "flex-end",
                  fontSize: "0.75rem",
                  color: greetingRemaining < 0 ? "#DC2626" : "#94A3B8",
                }}
              >
                {draft.assistant_greeting.length} /{" "}
                {settings.assistant_greeting_max_length} characters
              </Typography>

              <TextField
                label="Extra guidance"
                value={draft.tone_custom_instructions}
                onChange={(event) =>
                  updateDraft({ tone_custom_instructions: event.target.value })
                }
                error={!!fieldErrors.tone_custom_instructions}
                helperText={
                  fieldErrors.tone_custom_instructions ||
                  "Add a short note about how the assistant should sound. For example: 'Use calm, simple wording and avoid sounding salesy.'"
                }
                multiline
                minRows={4}
                fullWidth
                inputProps={{ maxLength: settings.tone_custom_instructions_max_length }}
              />
              <Typography
                sx={{
                  alignSelf: "flex-end",
                  fontSize: "0.75rem",
                  color: guidanceRemaining < 0 ? "#DC2626" : "#94A3B8",
                }}
              >
                {draft.tone_custom_instructions.length} /{" "}
                {settings.tone_custom_instructions_max_length} characters
              </Typography>

              {formError && <Alert severity="error">{formError}</Alert>}
              {patchMutation.isSuccess && (
                <Alert severity="success">
                  {patchMutation.data.safe_message}
                </Alert>
              )}
              {syncMutation.isError && (
                <Alert severity="error">{getErrorMessage(syncMutation.error)}</Alert>
              )}
              {syncMutation.isSuccess && (
                <Alert severity="success">
                  {syncMutation.data.safe_message}
                </Alert>
              )}

              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveOutlinedIcon />}
                  disabled={!isDirty || patchMutation.isPending || !settings.can_save}
                  onClick={handleSave}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    borderRadius: "10px",
                  }}
                >
                  {patchMutation.isPending ? "Saving..." : "Save settings"}
                </Button>
                {canShowSync && (
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<SyncRoundedIcon />}
                    disabled={isDirty || syncMutation.isPending}
                    onClick={() => syncMutation.mutate()}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      boxShadow: "none",
                      borderRadius: "10px",
                    }}
                  >
                    {syncMutation.isPending ? "Syncing..." : "Sync changes"}
                  </Button>
                )}
                <Button
                  variant="outlined"
                  startIcon={<VisibilityOutlinedIcon />}
                  disabled={
                    previewMutation.isPending ||
                    !settings.can_preview_assistant
                  }
                  onClick={() => previewMutation.mutate()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: "10px",
                  }}
                >
                  {previewMutation.isPending ? "Loading..." : "Preview assistant"}
                </Button>
                <Button
                  variant="text"
                  disabled={!isDirty}
                  onClick={() => {
                    if (settings) initDraft(settings);
                    else resetDraft();
                  }}
                  sx={{ textTransform: "none", fontWeight: 700 }}
                >
                  Cancel
                </Button>
              </Box>

              <FormHelperText sx={{ mx: 0 }}>
                Avoid service details, policies, prices, or booking rules here.
              </FormHelperText>
            </Stack>
          </CardContent>
        </Card>

        <ChatStatusSummary settings={settings} />

        <VoiceStatusSummary settings={settings} />

        {previewMutation.isError && (
          <Alert severity="error">Failed to load assistant preview.</Alert>
        )}

        <VoiceAssistantPreview
          sections={previewMutation.data?.sections ?? null}
          loading={previewMutation.isPending}
        />
      </Stack>
    </Box>
  );
};
